import React, { useCallback, useMemo } from 'react';
import {
  ColumnDef,
  CoreOptions,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  Row,
  useReactTable,
} from '@tanstack/react-table';
import styles from './Table.module.scss';
import classnames from 'classnames/bind';
import { ArrowIcon } from '../../assets/icons/ArrowIcon';
import { FinalBracket } from '../../assets/icons/FinalBracket';
import { MiddleBracket } from '../../assets/icons/MiddleBracket';
import { isEven } from '../../utils/is-even';
const cx = classnames.bind(styles);

function getBanding<TableData>(row: Row<TableData>) {
  const rootRow = row.getParentRows()[0] || row;
  return isEven(rootRow.index) ? 'even-row' : 'odd-row';
}

function isLastSubRow<TableData>(row: Row<TableData>) {
  const siblings = row.getParentRow()?.subRows;
  const lastSibling = siblings?.at(-1);
  return lastSibling?.id === row.id;
}

function handleExpandingColumn<TableData>(
  // Have to use any here. See: https://github.com/TanStack/table/issues/4241#issuecomment-1206657401
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  userColumns: ColumnDef<TableData, any>[],
  enableExpanding: boolean,
  shouldHighlight: (rowData: TableData) => boolean,
) {
  if (!enableExpanding) return userColumns;

  const columnHelper = createColumnHelper<TableData>();

  return [
    columnHelper.display({
      id: 'expand',
      size: 34,
      cell: ({ row }) => {
        if (row.getCanExpand())
          return (
            <div className={cx('expand-container')}>
              <button
                onClick={(event) => {
                  event.stopPropagation();
                  row.getToggleExpandedHandler()();
                }}
                aria-label={row.getIsExpanded() ? 'Collapse Row' : 'Expand Row'}
              >
                <ArrowIcon className={cx('arrow-icon', row.getIsExpanded() ? 'expanded' : 'closed')} />
              </button>
            </div>
          );
        if (shouldHighlight(row.original)) {
          return isLastSubRow(row) ? (
            <FinalBracket className={cx('bracket')} />
          ) : (
            <MiddleBracket className={cx('bracket')} />
          );
        }
        return null;
      },
    }),
    ...userColumns,
  ];
}

interface TableProps<TableData> {
  data: TableData[];
  // Have to use any here. See: https://github.com/TanStack/table/issues/4241#issuecomment-1206657401
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: ColumnDef<TableData, any>[];
  getSubRows?: CoreOptions<TableData>['getSubRows'];
  shouldHighlight?: (rowData: TableData) => boolean;
  isRowClickable?: (rowData: TableData) => false | ((rowData: TableData) => void);
  inViewTrackingRef?: (node?: Element | null) => void;
}

function Table<TableData>({
  data,
  columns: userColumns,
  getSubRows,
  shouldHighlight: shouldHighlightProp,
  isRowClickable: isRowClickableProp,
  inViewTrackingRef,
}: TableProps<TableData>) {
  const shouldHighlight = useCallback(shouldHighlightProp || (() => false), [shouldHighlightProp]);
  const isRowClickable = useCallback(isRowClickableProp || (() => false as const), [isRowClickableProp]);
  const bindOnRowClick = (rowData: TableData) => {
    const onRowClick = isRowClickable(rowData);
    return onRowClick ? () => onRowClick(rowData) : undefined;
  };

  const enableExpanding = !!getSubRows;

  const columns = useMemo(
    () => handleExpandingColumn(userColumns, enableExpanding, shouldHighlight),
    [userColumns, enableExpanding, shouldHighlight],
  );

  const tableInstance = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getSubRows,
    defaultColumn: {
      size: 0,
      minSize: 0,
    },
    renderFallbackValue: '-',
  });

  const footerGroups = tableInstance.getFooterGroups();
  const hasFooters = useMemo(() => {
    const footers = footerGroups.flatMap(({ headers }) => headers.map(({ column }) => column.columnDef.footer));
    const hasFooters = footers.some((footer) => footer);
    return hasFooters;
  }, [footerGroups]);

  return (
    <table className={cx('table')}>
      <thead className={cx('table-head')}>
        {tableInstance.getHeaderGroups().map((headerGroup) => (
          <tr key={headerGroup.id}>
            {headerGroup.headers.map((header) => {
              const headerSize = header.getSize();
              const style = headerSize ? { width: `${headerSize}px` } : {};
              return (
                <th key={header.id} style={style}>
                  {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                </th>
              );
            })}
          </tr>
        ))}
      </thead>
      <tbody>
        {tableInstance.getRowModel().rows.map((row) => (
          <tr
            key={row.id}
            className={cx(getBanding(row), {
              'highlight-row': shouldHighlight(row.original),
              clickable: isRowClickable(row.original),
            })}
            onClick={bindOnRowClick(row.original)}
            ref={inViewTrackingRef}
          >
            {row.getVisibleCells().map((cell) => (
              <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
            ))}
          </tr>
        ))}
      </tbody>
      {hasFooters && (
        <tfoot>
          {footerGroups.map((footerGroup) => (
            <tr key={footerGroup.id}>
              {footerGroup.headers.map((header) => (
                <th key={header.id}>
                  {header.isPlaceholder ? null : flexRender(header.column.columnDef.footer, header.getContext())}
                </th>
              ))}
            </tr>
          ))}
        </tfoot>
      )}
    </table>
  );
}

export default Table;
